import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import config from "../config";
import {
  applyAuthToken,
  clearStoredAuth,
  getStoredAuth,
} from "./auth_storage";
import {
  isPureSuperAdminShellRoute,
  isStoredSuperAdminSessionValid,
} from "./super_admin_auth_storage";
import {
  endImpersonation,
  isImpersonationSession,
} from "../services/authService";
import {
  ensureFreshAccessToken,
  shouldAttemptTokenRefresh,
} from "./token_session";
import i18n from "../i18n";
import { resolveAdatisMarket } from "./adatisMarket";

const { api } = config;

const LANGUAGE_STORAGE_KEY = "I18N_LANGUAGE";
const DEFAULT_LOCALE = "pt-BR";

interface RetriableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const resolveAcceptLanguage = (): string => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);

  if (stored) {
    return stored === "sp" ? "es" : stored;
  }

  return i18n.language || DEFAULT_LOCALE;
};

const isPublicEndpoint = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return url.includes("/public/booking/")
    || url.includes("/public/studio/")
    || url.includes("/public/email/")
    || url.includes("/public/appointments/")
    || url.includes("/public/appointment-reviews/")
    || url.includes("/public/loyalty/");

};

const isAuthEndpoint = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return url.includes("/login")
    || url.includes("/register")
    || url.includes("/auth/forgot-password")
    || url.includes("/refresh")
    || url.includes("/logout");
};

const shouldSkipTenantLoginRedirect = (): boolean =>
  isPureSuperAdminShellRoute() && isStoredSuperAdminSessionValid();

const redirectToLogin = (): void => {
  if (shouldSkipTenantLoginRedirect()) {
    return;
  }

  clearStoredAuth();

  if (window.location.pathname !== "/login") {
    const { pathname, search } = window.location;
    const returnPath = `${pathname}${search}`;
    const loginUrl =
      returnPath.startsWith("/") &&
      !returnPath.startsWith("//") &&
      !returnPath.includes("://")
        ? `/login?return=${encodeURIComponent(returnPath)}`
        : "/login";
    window.location.href = loginUrl;
  }
};

const redirectAfterImpersonationAuthFailure = (): void => {
  endImpersonation();

  const targetPath = "/super-admin/tenants";

  if (window.location.pathname !== targetPath) {
    window.location.href = targetPath;
  }
};

const redirectOnUnauthorized = (): void => {
  if (isImpersonationSession()) {
    redirectAfterImpersonationAuthFailure();
    return;
  }

  redirectToLogin();
};

type ApiErrorPayload = {
  message?: string;
  error?: {
    message?: string;
    code?: string;
    order_id?: number;
    details?: Record<string, string[]>;
    clients?: Array<{
      id: number;
      name: string;
      sessions_remaining: number;
    }>;
  };
};

const normalizeAxiosErrorData = async (error: AxiosError): Promise<void> => {
  const data = error.response?.data;

  if (!(data instanceof Blob)) {
    return;
  }

  const isJsonBlob =
    data.type.includes("json") || data.type === "" || data.size < 4096;

  if (!isJsonBlob) {
    return;
  }

  try {
    const text = await data.text();
    const parsed = JSON.parse(text) as ApiErrorPayload;

    if (error.response) {
      error.response.data = parsed;
    }
  } catch {
    // Keep original blob when parsing fails.
  }
};

const extractErrorMessage = (error: AxiosError): string => {
  const responseData = error.response?.data as ApiErrorPayload | undefined;

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.error?.message) {
    return responseData.error.message;
  }

  switch (error.response?.status) {
    case 500:
      return "Internal Server Error";
    case 502:
    case 503:
    case 504:
      return (
        responseData?.error?.message ||
        i18n.t("settings.subscription.upgrade.toast.error")
      );
    case 401:
      return "Unauthorized";
    case 404:
      return "Sorry! the data you are looking for could not be found";
    default:
      return error.message || "Request failed";
  }
};

const buildApiError = (error: AxiosError): {
  data: string;
  code?: string;
  orderId?: number;
  details?: Record<string, string[]>;
  clients?: Array<{
    id: number;
    name: string;
    sessions_remaining: number;
  }>;
} => {
  const responseData = error.response?.data as ApiErrorPayload | undefined;

  return {
    data: extractErrorMessage(error),
    code: responseData?.error?.code,
    orderId: responseData?.error?.order_id,
    details: responseData?.error?.details,
    clients: responseData?.error?.clients,
  };
};

const SUBSCRIPTION_INACTIVE_BYPASS_PATHS = [
  "/settings/subscription",
  "/suspended",
];

const redirectToSuspended = (): void => {
  const pathname = window.location.pathname;

  if (SUBSCRIPTION_INACTIVE_BYPASS_PATHS.includes(pathname)) {
    return;
  }

  window.location.href = "/suspended";
};

const isSubscriptionInactiveError = (error: AxiosError): boolean => {
  const responseData = error.response?.data as
    | { error?: { code?: string } }
    | undefined;

  return responseData?.error?.code === "subscription_inactive";
};

const isFeatureLockedPlanError = (error: AxiosError): boolean => {
  const responseData = error.response?.data as
    | { error?: { code?: string } }
    | undefined;

  return responseData?.error?.code === "FEATURE_LOCKED_PLAN";
};

const notifyFeatureLockedPlan = (error: AxiosError): void => {
  const responseData = error.response?.data as
    | { error?: { message?: string } }
    | undefined;
  const message =
    responseData?.error?.message ||
    i18n.t("plan.upgrade.description");

  toast.error(
    `${message} ${i18n.t("plan.upgrade.toastHint")}`,
    { toastId: "FEATURE_LOCKED_PLAN" }
  );
};

axios.defaults.baseURL = api.API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["Accept-Language"] = resolveAcceptLanguage();

const storedAuth = getStoredAuth();

if (storedAuth?.token) {
  applyAuthToken(storedAuth.token);
}

axios.interceptors.request.use((requestConfig) => {
  requestConfig.headers["Accept-Language"] = resolveAcceptLanguage();
  requestConfig.headers["X-Adatis-Market"] = resolveAdatisMarket();

  const auth = getStoredAuth();

  if (auth?.token) {
    requestConfig.headers.Authorization = `Bearer ${auth.token}`;
  }

  return requestConfig;
});

axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  async function (error: AxiosError) {
    const originalRequest = error.config as RetriableAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url) &&
      !isPublicEndpoint(originalRequest.url)
    ) {
      if (!shouldAttemptTokenRefresh()) {
        redirectOnUnauthorized();
        return Promise.reject(buildApiError(error));
      }

      originalRequest._retry = true;

      try {
        const newToken = await ensureFreshAccessToken({ force: true });

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        }

        redirectOnUnauthorized();
        return Promise.reject(buildApiError(error));
      } catch {
        redirectOnUnauthorized();
        return Promise.reject(buildApiError(error));
      }
    }

    if (status === 403 && isSubscriptionInactiveError(error)) {
      redirectToSuspended();
      await normalizeAxiosErrorData(error);
      return Promise.reject(buildApiError(error));
    }

    if (status === 403 && isFeatureLockedPlanError(error)) {
      notifyFeatureLockedPlan(error);
      await normalizeAxiosErrorData(error);
      return Promise.reject(buildApiError(error));
    }

    await normalizeAxiosErrorData(error);

    return Promise.reject(buildApiError(error));
  }
);

const setAuthorization = (token: string) => {
  applyAuthToken(token || null);
};

class APIClient {
  get = <T = unknown>(url: string, params?: object): Promise<T> => {
    if (params) {
      const queryString = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      return axios.get(`${url}?${queryString}`) as Promise<T>;
    }

    return axios.get(url) as Promise<T>;
  };

  create = <T = unknown>(url: string, data: unknown): Promise<T> => {
    return axios.post(url, data) as Promise<T>;
  };

  update = <T = unknown>(url: string, data: unknown): Promise<T> => {
    return axios.patch(url, data) as Promise<T>;
  };

  put = <T = unknown>(url: string, data: unknown): Promise<T> => {
    return axios.put(url, data) as Promise<T>;
  };

  delete = <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axios.delete(url, { ...config }) as Promise<T>;
  };
}

const getLoggedinUser = () => {
  return getStoredAuth();
};

export { APIClient, setAuthorization, getLoggedinUser };
